// export const API_URL = 'http://192.168.51.169:8000'; 

const API_URL = "https://api.christlightassembly.com";
// const API_URL = "http://127.0.0.1:8000/";

class ApiService {
  constructor() {
    this.baseUrl = API_URL;
  }

  // Helper method to get headers with auth token
  getHeaders(includeAuth = true) {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (includeAuth) {
      const token = localStorage.getItem('accessToken');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  // Helper method to handle API responses
  async handleResponse(response) {
    console.log(`API Response Status: ${response.status}`, response);
    
    if (!response.ok) {
      const error = await response.json();
      console.error('API Error:', error);
      throw new Error(error.error || error.detail || 'API request failed');
    }
    
    const data = await response.json();
    console.log('API Response Data:', data);
    return data;
  }

  // Authentication Methods
  async login(username, password) {
    console.log('Attempting login for:', username);
    try {
      // Try different common Django REST framework auth endpoints
      const endpoints = [
        '/api/auth/token/',  // DRF Token Authentication
        '/api/token/',       // DRF Simple JWT
        '/api/auth/login/',  // Custom auth
        '/api/users/login/'  // Current endpoint
      ];

      let response = null;

      for (const endpoint of endpoints) {
        try {
          console.log(`Trying endpoint: ${endpoint}`);
          response = await fetch(`${this.baseUrl}${endpoint}`, {
            method: 'POST',
            headers: this.getHeaders(false),
            body: JSON.stringify({ username, password }),
          });

          console.log(`Response from ${endpoint}:`, response.status);
          
          if (response.ok) {
            break;
          }
        } catch (error) {
          console.log(`Failed to try ${endpoint}:`, error);
        }
      }

      if (!response || !response.ok) {
        throw new Error('Could not find valid login endpoint. Please check your backend configuration.');
      }

      const data = await this.handleResponse(response);
      
      // Store tokens
      if (data.tokens) {
        localStorage.setItem('accessToken', data.tokens.access);
        localStorage.setItem('refreshToken', data.tokens.refresh);
      } else if (data.access) {
        // Handle simple JWT response
        localStorage.setItem('accessToken', data.access);
        localStorage.setItem('refreshToken', data.refresh);
      } else if (data.token) {
        // Handle token auth response
        localStorage.setItem('accessToken', data.token);
      }
      
      return data.user || data;
    } catch (error) {
      console.error('Login error:', error);
      throw new Error('Login failed. Please check your credentials and try again.');
    }
  }

  async logout() {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return;

    try {
      console.log('Attempting logout');
      await fetch(`${this.baseUrl}/users/logout/`, {
        method: 'POST',
        headers: this.getHeaders(false),
        body: JSON.stringify({ refresh: refreshToken }),
      });
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  }

  async refreshToken() {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) throw new Error('No refresh token available');

    console.log('Attempting token refresh');
    const response = await fetch(`${this.baseUrl}/token/refresh/`, {
      method: 'POST',
      headers: this.getHeaders(false),
      body: JSON.stringify({ refresh: refreshToken }),
    });

    const data = await this.handleResponse(response);
    localStorage.setItem('accessToken', data.access);
    return data.access;
  }

  // User Methods
  async getCurrentUser() {
    console.log('Fetching current user');
    try {
      const response = await fetch(`${this.baseUrl}/api/users/me/`, {
      headers: this.getHeaders(),
    });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Current User API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch current user');
      }

      const data = await response.json();
      console.log('Current user data:', data);
      return data;
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  }

  // Mentor Methods
  async createMentor(mentorData) {
    console.log('Creating mentor with data:', mentorData);
    try {
      // Ensure all required fields are present
      const requiredFields = ['username', 'email', 'name', 'password', 'confirm_password', 'phone', 'country'];
      const missingFields = requiredFields.filter(field => !mentorData[field]);
      
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const response = await fetch(`${this.baseUrl}/api/users/create_mentor/`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(mentorData),
      });

      console.log('Create mentor response:', response.status);
      
      const responseData = await response.json().catch(() => ({}));
      console.log('Create mentor response data:', responseData);

      if (!response.ok) {
        // Handle validation errors
        if (response.status === 400) {
          const errorMessages = Object.entries(responseData)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('\n');
          throw new Error(`Validation failed:\n${errorMessages}`);
        }
        throw new Error(responseData.detail || responseData.error || 'Failed to create mentor');
      }

      return responseData;
    } catch (error) {
      console.error('Error creating mentor:', error);
      throw error;
    }
  }

  async getMentors() {
    console.log('Fetching mentors list');
    try {
      const response = await fetch(`${this.baseUrl}/api/users/by_role/?role=mentor`, {
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch mentors');
      }

      const data = await response.json();
      console.log('Raw API response:', data);
      
      // Handle both paginated and non-paginated responses
      const mentors = data.results || data;
      console.log('Processed mentors:', mentors);
      
      return Array.isArray(mentors) ? mentors : [];
    } catch (error) {
      console.error('Error fetching mentors:', error);
      throw error;
    }
  }

  async getMentorById(id) {
    console.log('Fetching mentor by ID:', id);
    try {
      const response = await fetch(`${this.baseUrl}/api/users/${id}/`, {
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch mentor');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching mentor:', error);
      throw error;
    }
  }

  async updateMentor(id, mentorData) {
    console.log('Updating mentor:', id, mentorData);
    try {
      const response = await fetch(`${this.baseUrl}/api/users/${id}/`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(mentorData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.error || 'Failed to update mentor');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating mentor:', error);
      throw error;
    }
  }

  async deleteMentor(id) {
    console.log('Deleting mentor:', id);
    try {
      const response = await fetch(`${this.baseUrl}/api/users/${id}/`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.error || 'Failed to delete mentor');
      }

      return true;
    } catch (error) {
      console.error('Error deleting mentor:', error);
      throw error;
    }
  }

  async assignStudentToMentor(studentId, mentorId) {
    console.log('Assigning student:', studentId, 'to mentor:', mentorId);
    try {
      const response = await fetch(`${this.baseUrl}/api/users/${studentId}/assign_mentor/`, {
        method: 'POST',
        headers: {
          ...this.getHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mentor_id: mentorId }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Assign Student API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to assign student to mentor');
      }

      const data = await response.json();
      console.log('Student assigned to mentor:', data);
      return data;
    } catch (error) {
      console.error('Error assigning student to mentor:', error);
      throw error;
    }
  }

  // Dashboard Methods
  async getDashboardStats() {
    console.log('Fetching dashboard stats');
    try {
      const response = await fetch(`${this.baseUrl}/api/dashboard/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Dashboard Stats API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch dashboard stats');
      }

      const data = await response.json();
      console.log('Dashboard stats response:', data);
      return data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }

  // Quote Methods
  async getQuotes() {
    console.log('Fetching quotes list');
    try {
      const response = await fetch(`${this.baseUrl}/api/quotes/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Quotes API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch quotes');
      }

      const data = await response.json();
      console.log('Raw quotes API response:', data);
      
      // Handle both paginated and non-paginated responses
      const quotes = data.results || data;
      console.log('Processed quotes:', quotes);
      
      return Array.isArray(quotes) ? quotes : [];
    } catch (error) {
      console.error('Error fetching quotes:', error);
      throw error;
    }
  }

  async createQuote(quoteData) {
    console.log('Creating quote with data:', quoteData);
    try {
      // Add default author if not provided
      const quoteDataWithAuthor = {
        ...quoteData,
        author: "Anonymous" // Default author value
      };

      const response = await fetch(`${this.baseUrl}/api/quotes/`, {
        method: 'POST',
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(quoteDataWithAuthor)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Create Quote API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to create quote');
      }

      const data = await response.json();
      console.log('Created quote response:', data);
      return data;
    } catch (error) {
      console.error('Error creating quote:', error);
      throw error;
    }
  }

  async updateQuote(id, quoteData) {
    console.log('Updating quote:', id, quoteData);
    try {
      const response = await fetch(`${this.baseUrl}/api/quotes/${id}/`, {
        method: 'PUT',
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(quoteData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Update Quote API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to update quote');
      }

      const data = await response.json();
      console.log('Updated quote response:', data);
      return data;
    } catch (error) {
      console.error('Error updating quote:', error);
      throw error;
    }
  }

  async deleteQuote(id) {
    console.log('Deleting quote:', id);
    try {
      const response = await fetch(`${this.baseUrl}/api/quotes/${id}/`, {
        method: 'DELETE',
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete Quote API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to delete quote');
      }

      return true;
    } catch (error) {
      console.error('Error deleting quote:', error);
      throw error;
    }
  }

  // Flyer Methods
  async getFlyers() {
    console.log('Fetching flyers list');
    try {
      const response = await fetch(`${this.baseUrl}/api/flyers/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json',
          'Access-Control-Allow-Origin': '*'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch flyers');
      }

      const data = await response.json();
      console.log('Raw API response:', data);
      
      // Handle both paginated and non-paginated responses
      const flyers = data.results || data;
      
      // Ensure image URLs are complete and include credentials
      const processedFlyers = flyers.map(flyer => ({
        ...flyer,
        image: flyer.image ? (flyer.image.startsWith('http') ? flyer.image : `${this.baseUrl}${flyer.image}`) : null
      }));
      
      console.log('Processed flyers with complete image URLs:', processedFlyers);
      
      return Array.isArray(processedFlyers) ? processedFlyers : [];
    } catch (error) {
      console.error('Error fetching flyers:', error);
      throw error;
    }
  }

  async createFlyer(flyerData) {
    console.log('Creating flyer with data:', flyerData);
    try {
      // Create FormData object for file upload
      const formData = new FormData();
      formData.append('title', flyerData.title);
      formData.append('description', flyerData.description);
      formData.append('date', flyerData.date);
      if (flyerData.image) {
        formData.append('image', flyerData.image);
      }

      const response = await fetch(`${this.baseUrl}/api/flyers/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAccessToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.error || 'Failed to create flyer');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating flyer:', error);
      throw error;
    }
  }

  async updateFlyer(id, flyerData) {
    console.log('Updating flyer:', id, flyerData);
    try {
      // Create FormData object for file upload
      const formData = new FormData();
      formData.append('title', flyerData.title);
      formData.append('description', flyerData.description);
      formData.append('date', flyerData.date);
      if (flyerData.image) {
        formData.append('image', flyerData.image);
      }

      const response = await fetch(`${this.baseUrl}/api/flyers/${id}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.getAccessToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.error || 'Failed to update flyer');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating flyer:', error);
      throw error;
    }
  }

  async deleteFlyer(id) {
    console.log('Deleting flyer:', id);
    try {
      const response = await fetch(`${this.baseUrl}/api/flyers/${id}/`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.error || 'Failed to delete flyer');
      }

      return true;
    } catch (error) {
      console.error('Error deleting flyer:', error);
      throw error;
    }
  }

  // Event Methods
  async getEvents() {
    console.log('Fetching events list');
    try {
      const response = await fetch(`${this.baseUrl}/api/events/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Events API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch events');
      }

      const data = await response.json();
      console.log('Raw events API response:', data);
      
      // Handle both paginated and non-paginated responses
      const events = data.results || data;
      
      // Ensure image URLs are complete
      const processedEvents = events.map(event => ({
        ...event,
        image: event.image ? (event.image.startsWith('http') ? event.image : `${this.baseUrl}${event.image}`) : null
      }));
      
      console.log('Processed events:', processedEvents);
      
      return Array.isArray(processedEvents) ? processedEvents : [];
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }

  async createEvent(eventData) {
    console.log('Creating event with data:', eventData);
    try {
      // Create FormData object for file upload
      const formData = new FormData();
      formData.append('title', eventData.title);
      formData.append('description', eventData.description);
      formData.append('date', eventData.date);
      if (eventData.image) {
        formData.append('image', eventData.image);
      }

      const response = await fetch(`${this.baseUrl}/api/events/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAccessToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Create Event API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to create event');
      }

      const data = await response.json();
      console.log('Created event response:', data);
      return data;
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  }

  async updateEvent(id, eventData) {
    console.log('Updating event:', id, eventData);
    try {
      // Create FormData object for file upload
      const formData = new FormData();
      formData.append('title', eventData.title);
      formData.append('description', eventData.description);
      formData.append('date', eventData.date);
      if (eventData.image) {
        formData.append('image', eventData.image);
      }

      const response = await fetch(`${this.baseUrl}/api/events/${id}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.getAccessToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Update Event API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to update event');
      }

      const data = await response.json();
      console.log('Updated event response:', data);
      return data;
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  async deleteEvent(id) {
    console.log('Deleting event:', id);
    try {
      const response = await fetch(`${this.baseUrl}/api/events/${id}/`, {
        method: 'DELETE',
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete Event API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to delete event');
      }

      return true;
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }

  // Project Methods
  async getProjects() {
    try {
      console.log('Fetching projects...')
      const response = await fetch(`${this.baseUrl}/api/church-projects/`, {
        method: 'GET',
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      })

      if (!response.ok) {
        const responseText = await response.text();
        console.error('Error response (Text):', responseText);
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.detail || 'Failed to fetch projects');
        } catch (jsonError) {
          throw new Error(`API request failed with status ${response.status}. Response: ${responseText.substring(0, 100)}...`);
        }
      }

      const data = await response.json()
      console.log('Projects response:', data)
      return data
    } catch (error) {
      console.error('Error in getProjects:', error)
      throw error
    }
  }

  async createProject(projectData) {
    try {
      console.log('Creating project:', projectData)
      const formData = new FormData()
      formData.append('title', projectData.title)
      formData.append('description', projectData.description)
      formData.append('status', projectData.status)
      if (projectData.image) {
        formData.append('image', projectData.image)
      }

      const response = await fetch(`${this.baseUrl}/api/church-projects/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAccessToken()}`,
        },
        body: formData,
        credentials: 'include'
      })

      if (!response.ok) {
        const responseText = await response.text();
        console.error('Error response (Text):', responseText);
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.detail || 'Failed to create project');
        } catch (jsonError) {
          throw new Error(`API request failed with status ${response.status}. Response: ${responseText.substring(0, 100)}...`);
        }
      }

      const data = await response.json()
      console.log('Create project response:', data)
      return data
    } catch (error) {
      console.error('Error in createProject:', error)
      throw error
    }
  }

  async updateProject(id, projectData) {
    try {
      console.log('Updating project:', id, projectData)
      const formData = new FormData()
      formData.append('title', projectData.title)
      formData.append('description', projectData.description)
      formData.append('status', projectData.status)
      if (projectData.image) {
        formData.append('image', projectData.image)
      }

      const response = await fetch(`${this.baseUrl}/church-projects/${id}/`, {
        method: 'PUT',
        body: formData,
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Error response:', errorData)
        throw new Error(errorData.detail || 'Failed to update project')
      }

      const data = await response.json()
      console.log('Update project response:', data)
      return data
    } catch (error) {
      console.error('Error in updateProject:', error)
      throw error
    }
  }

  async deleteProject(id) {
    try {
      console.log('Deleting project:', id)
      const response = await fetch(`${this.baseUrl}/church-projects/${id}/`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Error response:', errorData)
        throw new Error(errorData.detail || 'Failed to delete project')
      }

      console.log('Project deleted successfully')
      return true
    } catch (error) {
      console.error('Error in deleteProject:', error)
      throw error
    }
  }

  // Devotion Methods
  async getDevotions() {
    console.log('Fetching devotions list');
    try {
      const response = await fetch(`${this.baseUrl}/api/devotions/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Devotions API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch devotions');
      }

      const data = await response.json();
      console.log('Raw devotions API response:', data);
      
      // Handle both paginated and non-paginated responses
      const devotions = data.results || data;
      console.log('Processed devotions:', devotions);
      
      return Array.isArray(devotions) ? devotions : [];
    } catch (error) {
      console.error('Error fetching devotions:', error);
      throw error;
    }
  }

  async createDevotion(devotionData) {
    console.log('Creating devotion with data:', devotionData);
    try {
      // Create FormData object for file upload
      const formData = new FormData();
      formData.append('title', devotionData.title);
      formData.append('description', devotionData.description);
      formData.append('date', devotionData.date);
      if (devotionData.video) {
        formData.append('video', devotionData.video);
      }
      if (devotionData.youtube_url) {
        formData.append('youtube_url', devotionData.youtube_url);
      }

      const response = await fetch(`${this.baseUrl}/api/devotions/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAccessToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Create Devotion API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to create devotion');
      }

      const data = await response.json();
      console.log('Created devotion response:', data);
      return data;
    } catch (error) {
      console.error('Error creating devotion:', error);
      throw error;
    }
  }

  async updateDevotion(id, devotionData) {
    console.log('Updating devotion:', id, devotionData);
    try {
      // Create FormData object for file upload
      const formData = new FormData();
      formData.append('title', devotionData.title);
      formData.append('description', devotionData.description);
      formData.append('date', devotionData.date);
      if (devotionData.video) {
        formData.append('video', devotionData.video);
      }
      if (devotionData.youtube_url) {
        formData.append('youtube_url', devotionData.youtube_url);
      }

      const response = await fetch(`${this.baseUrl}/api/devotions/${id}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.getAccessToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Update Devotion API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to update devotion');
      }

      const data = await response.json();
      console.log('Updated devotion response:', data);
      return data;
    } catch (error) {
      console.error('Error updating devotion:', error);
      throw error;
    }
  }

  async deleteDevotion(id) {
    console.log('Deleting devotion:', id);
    try {
      const response = await fetch(`${this.baseUrl}/api/devotions/${id}/`, {
        method: 'DELETE',
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete Devotion API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to delete devotion');
      }

      return true;
    } catch (error) {
      console.error('Error deleting devotion:', error);
      throw error;
    }
  }

  // Announcement Methods
  async getAnnouncements() {
    console.log('Fetching announcements list');
    try {
      const response = await fetch(`${this.baseUrl}/api/announcements/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Announcements API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch announcements');
      }

      const data = await response.json();
      console.log('Raw announcements API response:', data);
      
      // Handle both paginated and non-paginated responses
      const announcements = data.results || data;
      console.log('Processed announcements:', announcements);
      
      return Array.isArray(announcements) ? announcements : [];
    } catch (error) {
      console.error('Error fetching announcements:', error);
      throw error;
    }
  }

  async createAnnouncement(announcementData) {
    console.log('Creating announcement with data:', announcementData);
    try {
      // Create FormData object for file upload
      const formData = new FormData();
      formData.append('title', announcementData.title);
      formData.append('content', announcementData.content);
      formData.append('date', announcementData.date);
      if (announcementData.video) {
        formData.append('video', announcementData.video);
      }
      if (announcementData.youtube_url) {
        formData.append('youtube_url', announcementData.youtube_url);
      }

      const response = await fetch(`${this.baseUrl}/api/announcements/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAccessToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Create Announcement API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to create announcement');
      }

      const data = await response.json();
      console.log('Created announcement response:', data);
      return data;
    } catch (error) {
      console.error('Error creating announcement:', error);
      throw error;
    }
  }

  async updateAnnouncement(id, announcementData) {
    console.log('Updating announcement:', id, announcementData);
    try {
      // Create FormData object for file upload
      const formData = new FormData();
      formData.append('title', announcementData.title);
      formData.append('content', announcementData.content);
      formData.append('date', announcementData.date);
      if (announcementData.video) {
        formData.append('video', announcementData.video);
      }
      if (announcementData.youtube_url) {
        formData.append('youtube_url', announcementData.youtube_url);
      }

      const response = await fetch(`${this.baseUrl}/api/announcements/${id}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.getAccessToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Update Announcement API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to update announcement');
      }

      const data = await response.json();
      console.log('Updated announcement response:', data);
      return data;
    } catch (error) {
      console.error('Error updating announcement:', error);
      throw error;
    }
  }

  async deleteAnnouncement(id) {
    console.log('Deleting announcement:', id);
    try {
      const response = await fetch(`${this.baseUrl}/api/announcements/${id}/`, {
        method: 'DELETE',
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete Announcement API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to delete announcement');
      }

      return true;
    } catch (error) {
      console.error('Error deleting announcement:', error);
      throw error;
    }
  }

  // Video methods
  async getVideos(pageUrl = null) {
    try {
      const url = pageUrl || `${this.baseUrl}/api/videos/`
      console.log('Fetching videos from:', url)
      const response = await fetch(url, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('Videos API Error:', errorData)
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch videos')
      }

      const data = await response.json()
      console.log('Raw videos API response:', data)
      console.log('Processed videos:', data.results)
      return data
    } catch (error) {
      console.error('Error fetching videos:', error)
      throw error
    }
  }

  async createVideo(videoData) {
    try {
      console.log('Creating video with data:', videoData);
      const formData = new FormData();
      
      // Add basic fields
      formData.append('title', videoData.title);
      
      // Map frontend categories to API categories
      const categoryMap = {
        'praise': 'praise',
        'worship': 'worship',
        'sermons': 'sermon',
        'faith': 'faith',
        'healing': 'healing',
        'prayer': 'prayer',
        'spiritual_growth': 'spiritual_growth',
        'crusade': 'crusade',
        'lightspot': 'light_spot'
      };
      
      const apiCategory = categoryMap[videoData.category];
      if (!apiCategory) {
        throw new Error(`Invalid category: ${videoData.category}`);
      }
      
      formData.append('category', apiCategory);
      
      // Handle Light Spot specific validation and fields
      if (apiCategory === 'light_spot') {
        // Validate required fields for Light Spot
        if (!videoData.level || !videoData.day_number) {
          throw new Error('Level and day number are required for Light Spot videos');
        }

        // Validate day number range
        const maxDays = {
          basic: 120,
          intermediate: 20,
          advanced: 20
        };

        if (videoData.day_number < 1 || videoData.day_number > maxDays[videoData.level]) {
          throw new Error(`Day number must be between 1 and ${maxDays[videoData.level]} for ${videoData.level} level`);
        }

        formData.append('level', videoData.level);
        formData.append('day_number', videoData.day_number.toString());
        
        // Add assignment fields if present
        if (videoData.has_assignment !== undefined) {
          formData.append('has_assignment', videoData.has_assignment.toString());
          if (videoData.has_assignment && videoData.assignment_instructions) {
            formData.append('assignment_instructions', videoData.assignment_instructions);
          }
        }
      }
      
      // Add video content
      if (videoData.youtube_url) {
        formData.append('youtube_url', videoData.youtube_url);
      } else if (videoData.video) {
        formData.append('video', videoData.video);
      }

      const response = await fetch(`${this.baseUrl}/api/videos/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAccessToken()}`,
        },
        body: formData
      });

      if (!response.ok) {
        const responseText = await response.text();
        console.error('Error response text:', responseText);
        
        let errorData;
        try {
          errorData = JSON.parse(responseText);
          console.error('Parsed error data:', errorData);
          
          // Handle specific validation errors
          if (errorData.level) {
            throw new Error(errorData.level[0]);
          }
          if (errorData.day_number) {
            throw new Error(errorData.day_number[0]);
          }
          if (errorData.category) {
            throw new Error(`Invalid category: ${errorData.category.join(', ')}`);
          }
        } catch (e) {
          console.error('Failed to parse error response as JSON:', e);
          errorData = { detail: responseText };
        }
        
        throw new Error(errorData.detail || errorData.error || 'Failed to create video');
      }

      const data = await response.json();
      console.log('Created video response:', data);
      return data;
    } catch (error) {
      console.error('Error creating video:', error);
      throw error;
    }
  }

  async updateVideo(id, videoData) {
    try {
      const formData = new FormData();
      
      // Add basic fields
      formData.append('title', videoData.title);
      formData.append('category', videoData.category);
      
      // Add Light Spot specific fields if present
      if (videoData.category === 'light_spot') {
        formData.append('level', videoData.level);
        formData.append('day_number', videoData.day_number);
      }
      
      // Add video content
      if (videoData.youtube_url) {
        formData.append('youtube_url', videoData.youtube_url);
      } else if (videoData.video) {
        formData.append('video', videoData.video);
      }

      const response = await fetch(`${this.baseUrl}/api/videos/${id}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.getAccessToken()}`,
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Update Video API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to update video');
      }

      const data = await response.json();
      console.log('Updated video response:', data);
      return data;
    } catch (error) {
      console.error('Error updating video:', error);
      throw error;
    }
  }

  async deleteVideo(id) {
    try {
      const response = await fetch(`${this.baseUrl}/api/videos/${id}/`, {
        method: 'DELETE',
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete Video API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to delete video');
      }

      return true;
    } catch (error) {
      console.error('Error deleting video:', error);
      throw error;
    }
  }

  // Prayer Request Methods
  async getPrayerRequests() {
    console.log('Fetching prayer requests list');
    try {
      const response = await fetch(`${this.baseUrl}/api/prayer-requests/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Prayer Requests API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch prayer requests');
      }

      const data = await response.json();
      console.log('Raw prayer requests API response:', data);
      
      // Handle both paginated and non-paginated responses
      const requests = data.results || data;
      console.log('Processed prayer requests:', requests);
      
      return Array.isArray(requests) ? requests : [];
    } catch (error) {
      console.error('Error fetching prayer requests:', error);
      throw error;
    }
  }

  async getPrayerRequestById(id) {
    console.log('Fetching prayer request:', id);
    try {
      const response = await fetch(`${this.baseUrl}/api/prayer-requests/${id}/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Prayer Request API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch prayer request');
      }

      const data = await response.json();
      console.log('Prayer request details:', data);
      return data;
    } catch (error) {
      console.error('Error fetching prayer request:', error);
      throw error;
    }
  }

  async updatePrayerRequestStatus(id, status) {
    console.log('Updating prayer request status:', id, status);
    try {
      const response = await fetch(`${this.baseUrl}/api/prayer-requests/${id}/`, {
        method: 'PATCH',
        headers: {
          ...this.getHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Update Prayer Request API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to update prayer request');
      }

      const data = await response.json();
      console.log('Updated prayer request:', data);
      return data;
    } catch (error) {
      console.error('Error updating prayer request:', error);
      throw error;
    }
  }

  async deletePrayerRequest(id) {
    console.log('Deleting prayer request:', id);
    try {
      const response = await fetch(`${this.baseUrl}/api/prayer-requests/${id}/`, {
        method: 'DELETE',
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete Prayer Request API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to delete prayer request');
      }

      return true;
    } catch (error) {
      console.error('Error deleting prayer request:', error);
      throw error;
    }
  }

  // Testimony Methods
  async getTestimonies() {
    console.log('Fetching testimonies list');
    try {
      const response = await fetch(`${this.baseUrl}/api/testimonies/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Testimonies API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch testimonies');
      }

      const data = await response.json();
      console.log('Raw testimonies API response:', data);
      
      // Handle both paginated and non-paginated responses
      const testimonies = data.results || data;
      console.log('Processed testimonies:', testimonies);
      
      return Array.isArray(testimonies) ? testimonies : [];
    } catch (error) {
      console.error('Error fetching testimonies:', error);
      throw error;
    }
  }

  async getTestimonyById(id) {
    console.log('Fetching testimony:', id);
    try {
      const response = await fetch(`${this.baseUrl}/api/testimonies/${id}/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Testimony API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch testimony');
      }

      const data = await response.json();
      console.log('Testimony details:', data);
      return data;
    } catch (error) {
      console.error('Error fetching testimony:', error);
      throw error;
    }
  }

  async updateTestimony(id, testimonyData) {
    console.log('Updating testimony:', id, testimonyData);
    try {
      const response = await fetch(`${this.baseUrl}/api/testimonies/${id}/`, {
        method: 'PUT',
        headers: {
          ...this.getHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(testimonyData),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Update Testimony API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to update testimony');
      }

      const data = await response.json();
      console.log('Updated testimony:', data);
      return data;
    } catch (error) {
      console.error('Error updating testimony:', error);
      throw error;
    }
  }

  async deleteTestimony(id) {
    console.log('Deleting testimony:', id);
    try {
      const response = await fetch(`${this.baseUrl}/api/testimonies/${id}/`, {
        method: 'DELETE',
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete Testimony API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to delete testimony');
      }

      return true;
    } catch (error) {
      console.error('Error deleting testimony:', error);
      throw error;
    }
  }

  // Light Spot Methods
  async getLightSpotRequests() {
    console.log('Fetching Light Spot requests');
    try {
      const response = await fetch(`${this.baseUrl}/api/light-spot-requests/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Light Spot Requests API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch Light Spot requests');
      }

      const data = await response.json();
      console.log('Raw Light Spot requests response:', data);
      
      // Handle both paginated and non-paginated responses
      const requests = data.results || data;
      console.log('Processed Light Spot requests:', requests);
      
      return Array.isArray(requests) ? requests : [];
    } catch (error) {
      console.error('Error fetching Light Spot requests:', error);
      throw error;
    }
  }

  async getLightSpotRequestById(id) {
    console.log('Fetching Light Spot request:', id);
    try {
      const response = await fetch(`${this.baseUrl}/api/light-spot-requests/${id}/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Light Spot Request API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch Light Spot request');
      }

      const data = await response.json();
      console.log('Light Spot request details:', data);
      return data;
    } catch (error) {
      console.error('Error fetching Light Spot request:', error);
      throw error;
    }
  }

  async approveLightSpotRequest(id, mentorId) {
    console.log('Approving Light Spot request:', id, 'with mentor:', mentorId);
    try {
      if (!mentorId) {
        throw new Error('Mentor ID is required to approve a Light Spot request');
      }

      const url = `${this.baseUrl}/api/light-spot-requests/${id}/approve/`;
      console.log('Making request to:', url);
      
      const headers = {
          ...this.getHeaders(),
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      console.log('Request headers:', headers);

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ mentor_id: mentorId }),
        credentials: 'include'
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const responseText = await response.text();
        console.error('Error response text:', responseText);
        
        let errorData;
        try {
          errorData = JSON.parse(responseText);
          console.error('Parsed error data:', errorData);
        } catch (e) {
          console.error('Failed to parse error response as JSON:', e);
          errorData = { detail: responseText };
        }
        
        throw new Error(errorData.detail || errorData.error || 'Failed to approve Light Spot request');
      }

      // Check if there's any content to parse
      const contentType = response.headers.get('content-type');
      console.log('Response content type:', contentType);
      
      if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
        console.log('Approved Light Spot request response:', data);
      return data;
      }

      // If no content, return success
      console.log('No content in response, returning success');
      return { success: true };
    } catch (error) {
      console.error('Error in approveLightSpotRequest:', error);
      console.error('Error stack:', error.stack);
      throw error;
    }
  }

  async rejectLightSpotRequest(id) {
    console.log('Starting Light Spot request deletion for ID:', id);
    try {
      const url = `${this.baseUrl}/api/light-spot-requests/${id}/`;
      console.log('Making DELETE request to:', url);
      
      const headers = {
          ...this.getHeaders(),
        'Accept': 'application/json'
      };
      console.log('Request headers:', headers);

      const response = await fetch(url, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const responseText = await response.text();
        console.error('Error response text:', responseText);
        
        let errorData;
        try {
          errorData = JSON.parse(responseText);
          console.error('Parsed error data:', errorData);
        } catch (e) {
          console.error('Failed to parse error response as JSON:', e);
          errorData = { detail: responseText };
        }
        
        throw new Error(errorData.detail || errorData.error || 'Failed to delete Light Spot request');
      }

      console.log('Successfully deleted Light Spot request');
      return { success: true };
    } catch (error) {
      console.error('Error in rejectLightSpotRequest:', error);
      console.error('Error stack:', error.stack);
      throw error;
    }
  }

  async getLightSpotAccounts() {
    console.log('Fetching Light Spot accounts');
    try {
      const response = await fetch(`${this.baseUrl}/api/light-spot-accounts/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Light Spot Accounts API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch Light Spot accounts');
      }

      const data = await response.json();
      console.log('Raw Light Spot accounts response:', data);
      
      // Handle both paginated and non-paginated responses
      const accounts = data.results || data;
      console.log('Processed Light Spot accounts:', accounts);
      
      return Array.isArray(accounts) ? accounts : [];
    } catch (error) {
      console.error('Error fetching Light Spot accounts:', error);
      throw error;
    }
  }

  async getLightSpotAccountById(id) {
    console.log('Fetching Light Spot account:', id);
    try {
      const response = await fetch(`${this.baseUrl}/api/light-spot-accounts/${id}/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Light Spot Account API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch Light Spot account');
      }

      const data = await response.json();
      console.log('Light Spot account details:', data);
      return data;
    } catch (error) {
      console.error('Error fetching Light Spot account:', error);
      throw error;
    }
  }

  async updateLightSpotAccount(id, accountData) {
    console.log('Updating Light Spot account:', id, accountData);
    try {
      const response = await fetch(`${this.baseUrl}/api/light-spot-accounts/${id}/`, {
        method: 'PUT',
        headers: {
          ...this.getHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(accountData),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Update Light Spot Account API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to update Light Spot account');
      }

      const data = await response.json();
      console.log('Updated Light Spot account:', data);
      return data;
    } catch (error) {
      console.error('Error updating Light Spot account:', error);
      throw error;
    }
  }

  async deactivateLightSpotAccount(id) {
    console.log('Deactivating Light Spot account:', id);
    try {
      const response = await fetch(`${this.baseUrl}/api/light-spot-accounts/${id}/deactivate/`, {
        method: 'POST',
        headers: {
          ...this.getHeaders(),
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Deactivate Light Spot Account API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to deactivate Light Spot account');
      }

      const data = await response.json();
      console.log('Deactivated Light Spot account:', data);
      return data;
    } catch (error) {
      console.error('Error deactivating Light Spot account:', error);
      throw error;
    }
  }

  // Mentor Dashboard Methods
  async getAssignments() {
    console.log('Fetching assignments');
    try {
      const response = await fetch(`${this.baseUrl}/api/assignments/`, {
        method: 'GET',
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Assignments API Error:', errorData);
        // Return empty array instead of throwing error to prevent dashboard from breaking
        return [];
      }

      const data = await response.json();
      console.log('Raw assignments API response:', data);
      
      // Handle both paginated and non-paginated responses
      const assignments = data.results || data;
      console.log('Processed assignments:', assignments);
      
      return Array.isArray(assignments) ? assignments : [];
    } catch (error) {
      console.error('Error fetching assignments:', error);
      // Return empty array instead of throwing error to prevent dashboard from breaking
      return [];
    }
  }

  async getMessages() {
    console.log('Fetching messages');
    try {
      const response = await fetch(`${this.baseUrl}/api/messages/`, {
        method: 'GET',
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Messages API Error:', errorData);
        // Return empty array instead of throwing error to prevent dashboard from breaking
        return [];
      }

      const data = await response.json();
      console.log('Raw messages API response:', data);
      
      // Handle both paginated and non-paginated responses
      const messages = data.results || data;
      console.log('Processed messages:', messages);
      
      return Array.isArray(messages) ? messages : [];
    } catch (error) {
      console.error('Error fetching messages:', error);
      // Return empty array instead of throwing error to prevent dashboard from breaking
      return [];
    }
  }

  async getStudents() {
    console.log('=== Starting getStudents ===');
    try {
      // Get current user to get mentor ID
      console.log('Fetching current user...');
      const currentUser = await this.getCurrentUser();
      console.log('Current user data:', currentUser);

      if (currentUser.user_type !== 'mentor') {
        throw new Error('Only mentors can access their students');
      }

      // Use the correct endpoint for mentor's students
      const url = `${this.baseUrl}/api/assignments/my_students/`;
      console.log('Making request to:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Students API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch students');
      }

      const data = await response.json();
      console.log('Raw students API response:', data);
      
      // Handle both paginated and non-paginated responses
      const students = data.results || data;
      console.log('Total students:', students.length);
      console.log('All students:', students);
      
      return Array.isArray(students) ? students : [];
    } catch (error) {
      console.error('Error in getStudents:', error);
      throw error;
    } finally {
      console.log('=== End getStudents ===');
    }
  }

  // Assignment Methods
  async getStudentAssignments(studentId) {
    console.log('=== Starting getStudentAssignments ===');
    console.log('Student ID:', studentId);
    try {
      const url = `${this.baseUrl}/api/assignments/?student=${studentId}`;
      console.log('Making request to:', url);
      
      const response = await fetch(url, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Student Assignments API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch student assignments');
      }

      const data = await response.json();
      console.log('Raw API response:', data);
      
      // Handle both paginated and non-paginated responses
      const assignments = data.results || data;
      console.log('Processed assignments:', assignments);
      console.log('Number of assignments:', assignments.length);
      
      if (!Array.isArray(assignments)) {
        console.error('Expected array of assignments but got:', typeof assignments);
      return [];
      }
      
      return assignments;
    } catch (error) {
      console.error('Error in getStudentAssignments:', error);
      console.error('Error stack:', error.stack);
      throw error;
    } finally {
      console.log('=== End getStudentAssignments ===');
    }
  }

  async getAssignmentSubmissionDetails(assignmentId) {
    console.log('Fetching submission details for assignment:', assignmentId);
    try {
      const response = await fetch(`${this.baseUrl}/api/assignments/${assignmentId}/submission_details/`, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Assignment Details API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch assignment details');
      }

      const data = await response.json();
      console.log('Assignment details:', data);
      return data;
    } catch (error) {
      console.error('Error fetching assignment details:', error);
      throw error;
    }
  }

  async submitAssignmentFeedback(assignmentId, feedbackData) {
    console.log('Submitting feedback for assignment:', assignmentId, feedbackData);
    try {
      const response = await fetch(`${this.baseUrl}/api/assignments/${assignmentId}/submit_feedback/`, {
        method: 'POST',
        headers: {
          ...this.getHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(feedbackData),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Submit Feedback API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to submit feedback');
      }

      const data = await response.json();
      console.log('Feedback submitted:', data);
      return data;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  }

  // Assignment Methods
  async getStudentSubmissions(studentId) {
    console.log('=== Starting getStudentSubmissions ===');
    console.log('Student ID:', studentId);
    try {
      const url = `${this.baseUrl}/api/assignments/student_submissions/?student_id=${studentId}`;
      console.log('Making request to:', url);
      
      const response = await fetch(url, {
        headers: {
          ...this.getHeaders(),
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Student Submissions API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to fetch student submissions');
      }

      const data = await response.json();
      console.log('Raw API response:', data);
      
      // Extract assignments from the response structure
      const assignments = data.assignments || [];
      console.log('Processed assignments:', assignments);
      console.log('Number of assignments:', assignments.length);
      
      if (!Array.isArray(assignments)) {
        console.error('Expected array of assignments but got:', typeof assignments);
        return [];
      }
      
      return assignments;
    } catch (error) {
      console.error('Error in getStudentSubmissions:', error);
      console.error('Error stack:', error.stack);
      throw error;
    } finally {
      console.log('=== End getStudentSubmissions ===');
    }
  }

  // Messages
  async getConversations() {
    const response = await fetch(`${this.baseUrl}/api/messages/conversations/`, {
      headers: this.getHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch conversations');
    }
    return response.json();
  }

  async getConversationWith(userId) {
    const response = await fetch(`${this.baseUrl}/api/messages/conversation_with/?user_id=${userId}`, {
      headers: this.getHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch conversation');
    }
    return response.json();
  }

  async sendMessage(data) {
    try {
      console.log('Sending message with data:', data);
      const response = await fetch(`${this.baseUrl}/api/messages/`, {
        method: 'POST',
        headers: {
          ...this.getHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data),
        credentials: 'include'
      });

      console.log('Message send response status:', response.status);
      console.log('Message send response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Send Message API Error:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Failed to send message');
      }

      const responseData = await response.json();
      console.log('Message sent successfully:', responseData);
      return responseData;
    } catch (error) {
      console.error('Error in sendMessage:', error);
      throw error;
    }
  }

  async markMessageAsRead(messageId) {
    const response = await fetch(`${this.baseUrl}/api/messages/${messageId}/mark_as_read/`, {
      method: 'POST',
      headers: this.getHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to mark message as read');
    }
    return response.json();
  }

  // Utility Methods
  isAuthenticated() {
    return !!localStorage.getItem('accessToken');
  }

  getAccessToken() {
    return localStorage.getItem('accessToken');
  }
}

// Create and export a single instance
export const apiService = new ApiService(); 